import { Box, Button, Collapse, HStack, IconButton, Input, Stack, Text } from '@chakra-ui/react';
import { Controller, useFieldArray, useFormState } from 'react-hook-form';
import { isEmail } from 'validator';
import pluralize from 'pluralize';

import { CloseIcon } from '@assets/icons';
import { useUserStore } from '@store/app.store';
import { useBreakpoints } from '@util/hooks';
import StyledSelect from '@components/general/styled-select.component';

/**
 * @param {Object} props
 * @param {UseForm<OnboardingFormValues>} props.form
 */
const AddUsersStep = ({ form }) => {
  const tenant = useUserStore((state) => state.tenant);
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'users' });
  const { errors, touchedFields } = useFormState({
    control: form.control
  });
  const { isMobile } = useBreakpoints();

  const numOfLicensesTotal = tenant?.internal_config?.num_of_licenses;
  const numOfLicensesAvailable = numOfLicensesTotal - fields?.length;

  const infoText = (
    <Text
      fontSize='sm'
      color='surface.onSurfaceVariant'
    >
      New users will receive an email invitation to join. You can purchase additional licenses later
      if needed.
    </Text>
  );

  return (
    <Stack
      h='full'
      w='full'
      justify='space-around'
    >
      {numOfLicensesTotal && (
        <Box
          data-testid='licenses-count'
          w='fit-content'
          bgColor='surface.containerLow'
          rounded='md'
          border='reskin'
          p={3}
        >
          <Text>
            You have{' '}
            <Text
              as='span'
              fontWeight={500}
            >
              {numOfLicensesAvailable} {pluralize('license', numOfLicensesAvailable)} available
            </Text>{' '}
          </Text>
        </Box>
      )}
      {isMobile && infoText}
      <Stack
        overflowY='scroll'
        h={64}
        borderY='reskin'
        py={2}
        pr={2}
      >
        {fields.map((field, i) => {
          const error = errors?.users?.[i]?.email;
          const showError = error && touchedFields.users?.[i];
          const typeOptions = field.isExisting
            ? [field.type || 'Existing']
            : ['Full', 'Executive', 'Admin'];
          return (
            <Stack key={field.id}>
              <HStack>
                <Input
                  disabled={field.isExisting}
                  type='email'
                  placeholder='name@email.com'
                  {...form.register(`users.${i}.email`, {
                    required: 'Must not be empty',
                    validate: (v) => isEmail(v) || 'Must be valid email'
                  })}
                />
                <Controller
                  name={`users.${i}.type`}
                  control={form.control}
                  render={({ field, formState }) => {
                    const isExisting = formState?.defaultValues?.users?.[i]?.isExisting;
                    return (
                      <StyledSelect
                        {...field}
                        menuButtonProps={{ minW: 32, h: 'full', disabled: isExisting }}
                        variant='reskin'
                        options={typeOptions.map((v) => ({
                          value: v,
                          label: v
                        }))}
                      />
                    );
                  }}
                />

                <IconButton
                  variant='reskinGhost'
                  colorScheme='surface'
                  isRound
                  size='sm'
                  icon={<CloseIcon />}
                  onClick={() => remove(i)}
                  disabled={field.isExisting}
                />
              </HStack>
              <Collapse in={showError}>
                <Text
                  fontSize='sm'
                  color='critical.primary'
                >
                  {error?.message}
                </Text>
              </Collapse>
            </Stack>
          );
        })}
      </Stack>
      <HStack gap={4}>
        <Button
          w='min-content'
          isDisabled={!numOfLicensesTotal || numOfLicensesAvailable === 0}
          variant='reskinLink'
          onClick={() => append({ email: '', type: 'Full' })}
        >
          Add another user
        </Button>
        {!isMobile && infoText}
      </HStack>
    </Stack>
  );
};

export default AddUsersStep;
